import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Zap,
  Building2,
  TrendingUp,
  Gauge,
  Activity,
  AlertTriangle,
  ArrowRight,
  MapPin,
} from 'lucide-react';
import { stateData, districts, generationTrend, alerts } from '../mock';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [selectedDistrict, setSelectedDistrict] = useState('All');

  const getKpiData = () => {
    if (selectedDistrict === 'All') {
      return {
        capacity: `${stateData.totalCapacity} MW`,
        sites: stateData.totalSites,
        generation: `${stateData.todayGeneration} kWh`,
        cuf: `${stateData.monthlyCUF}%`,
        pr: `${stateData.averagePR}%`,
        tickets: stateData.openTickets,
      };
    }
    const district = districts.find(d => d.name === selectedDistrict);
    if (district) {
      return {
        capacity: `${district.capacity} MW`,
        sites: district.sites,
        generation: `${district.todayGen} kWh`,
        cuf: `${district.cuf}%`,
        pr: `${district.pr}%`,
        tickets: 'N/A', // Assuming ticket data is not available per district in mock
      };
    }
    return {};
  };

  const kpiData = getKpiData();

  const kpiCards = [
    { icon: Zap, label: 'Total Capacity Installed', value: kpiData.capacity, color: '#10ac84' },
    { icon: Building2, label: 'Total Sites Connected', value: kpiData.sites, color: '#0a3d62', path: '/site-directory' },
    { icon: TrendingUp, label: "Today's Generation", value: kpiData.generation, color: '#3498db' },
    { icon: Gauge, label: 'Monthly CUF', value: kpiData.cuf, color: '#9b59b6' },
    { icon: Activity, label: 'Average PR', value: kpiData.pr, color: '#f39c12' },
    { icon: AlertTriangle, label: 'Open Tickets', value: kpiData.tickets, color: '#e74c3c', path: '/complaints' },
  ];

  const getStatusColor = (status) => {
    if (status === 'good') return '#10ac84';
    if (status === 'moderate') return '#f39c12';
    return '#e74c3c';
  };

  const getAlertBadgeStyle = (type) => {
    if (type === 'critical') return { backgroundColor: '#fee', color: '#e74c3c', borderColor: '#e74c3c' };
    if (type === 'warning') return { backgroundColor: '#fff4e6', color: '#f97316', borderColor: '#f97316' };
    return { backgroundColor: '#e8f5f1', color: '#10ac84', borderColor: '#10ac84' };
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>State Dashboard</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>Bihar Solar Power Monitoring - Real-time Overview</p>
      </div>

      {/* New Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Bihar Map */}
        <div className="lg:col-span-1">
          <Card className="h-full">
            <CardContent className="p-0">
              <img
                src="https://fcs-solar.web.app/g902ce71/assets/images/bihar-district.png"
                alt="Bihar Districts Map"
                className="w-full h-full object-cover rounded-lg"
              />
            </CardContent>
          </Card>
        </div>

        {/* Right: Aggregated Data */}
        <div className="lg:col-span-2">
          <div className="flex justify-end mb-4">
            <Select onValueChange={setSelectedDistrict} defaultValue={selectedDistrict}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select District" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                {districts.map((district) => (
                  <SelectItem key={district.id} value={district.name}>
                    {district.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {kpiCards.map((kpi, idx) => (
              <div key={idx} onClick={() => kpi.path && navigate(kpi.path)} className={kpi.path ? 'cursor-pointer' : ''}>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${kpi.color}15` }}>
                        <kpi.icon className="w-6 h-6" style={{ color: kpi.color }} />
                      </div>
                    </div>
                    <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{kpi.value}</div>
                    <div className="text-sm" style={{ color: '#576574' }}>{kpi.label}</div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Generation Trend & Performance */}
      <div className="lg:col-span-1 grid grid-cols-1 gap-6">
        {/* Generation Trend */}
        <div>
          <Card className="bg-transparent shadow-none border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" style={{ color: '#3498db' }} />
                7-Day Generation Trend {selectedDistrict !== 'All' && `- ${selectedDistrict}`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <Line
                  data={{
                    labels: generationTrend.map(day => 
                      new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    ),
                    datasets: [{
                      label: 'Generation (kWh)',
                      data: selectedDistrict === 'All' 
                        ? generationTrend.map(day => day.generation)
                        : generationTrend.map(day => 
                            (day.generation * (districts.find(d => d.name === selectedDistrict)?.capacity || 0) / stateData.totalCapacity).toFixed(0)
                          ),
                      borderColor: '#3498db',
                      backgroundColor: 'rgba(52, 152, 219, 0.1)',
                      tension: 0.4,
                      fill: true,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                        ticks: {
                          color: '#576574',
                        }
                      },
                      x: {
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)',
                        },
                        ticks: {
                          color: '#576574',
                        }
                      }
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Performance Gauges */}
        <div className="space-y-6">
          <Card className="bg-transparent shadow-none border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gauge className="w-5 h-5" style={{ color: '#f39c12' }} />
                Performance Ratio (PR)
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="text-4xl font-bold" style={{ color: '#f39c12' }}>{kpiData.pr}</div>
              <Progress value={parseFloat(kpiData.pr)} className="h-2 mt-2" />
            </CardContent>
          </Card>
          <Card className="bg-transparent shadow-none border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" style={{ color: '#9b59b6' }} />
                Capacity Utilization Factor (CUF)
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="text-4xl font-bold" style={{ color: '#9b59b6' }}>{kpiData.cuf}</div>
              <Progress value={parseFloat(kpiData.cuf) * 5} className="h-2 mt-2" />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Alerts Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" style={{ color: '#e74c3c' }} />
              Real-time Alerts
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/escalations')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div key={alert.id} className="p-4 rounded-lg border flex items-start gap-4 hover:shadow-md transition-shadow">
                <Badge variant="outline" className="px-3 py-1" style={getAlertBadgeStyle(alert.type)}>
                  {alert.type.toUpperCase()}
                </Badge>
                <div className="flex-1">
                  <p className="font-medium" style={{ color: '#0a3d62' }}>{alert.message}</p>
                  <p className="text-sm mt-1" style={{ color: '#576574' }}>{alert.timestamp}</p>
                </div>
                {alert.site && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/site/${alert.site}`)}>
                    View Site
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
